import { useCallback, useEffect, useState } from 'react';
import {
  downloadProductImageValidationRun,
  getProductImageValidationRun,
  getProductImageValidationStatus,
  startProductImageValidationRun,
} from './service';
import {
  ProductImageValidationJob,
  ProductImageValidationRun,
  ProductImageValidationCategoryRoot,
  ProductImageValidationScope,
} from './types';

function getErrorMessage(error: any): string {
  return error?.response?.data?.message || error?.message || 'Kunde inte köra bildkontrollen.';
}

export function useProductImageValidationRunner(options?: {
  onRunFinished?: (run: ProductImageValidationRun) => void;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<ProductImageValidationJob | null>(null);
  const [run, setRun] = useState<ProductImageValidationRun | null>(null);
  const [starting, setStarting] = useState(false);
  const [loadingRun, setLoadingRun] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadRun = useCallback(async (runId: string, notifyFinished: boolean) => {
    setLoadingRun(true);
    try {
      const nextRun = await getProductImageValidationRun(runId);
      setRun(nextRun);
      if (notifyFinished) {
        options?.onRunFinished?.(nextRun);
      }
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setLoadingRun(false);
    }
  }, [options]);

  const startRun = useCallback(async (
    scope: ProductImageValidationScope,
    assetIds?: string[],
    categoryRoot?: ProductImageValidationCategoryRoot,
  ) => {
    setDialogOpen(true);
    setError(null);
    setRun(null);
    setJob(null);
    setStarting(true);

    try {
      const started = await startProductImageValidationRun(scope, assetIds, categoryRoot);
      setJobId(started.jobId);
      setJob({
        jobId: started.jobId,
        scope,
        startedAt: new Date().toISOString(),
        current: 0,
        totalAssets: started.totalAssets,
        passCount: 0,
        failedCount: 0,
        errorCount: 0,
        done: false,
      });
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setStarting(false);
    }
  }, []);

  const openRun = useCallback(async (runId: string) => {
    setDialogOpen(true);
    setError(null);
    setJobId(null);
    setJob(null);
    setRun(null);
    await loadRun(runId, false);
  }, [loadRun]);

  const closeDialog = useCallback(() => {
    if (jobId) return;
    setDialogOpen(false);
  }, [jobId]);

  const downloadRun = useCallback(async (format: 'json' | 'csv' | 'txt') => {
    if (!run) return;
    await downloadProductImageValidationRun(run.runId, format);
  }, [run]);

  useEffect(() => {
    if (!jobId) return;

    let cancelled = false;
    const poll = async () => {
      try {
        const nextJob = await getProductImageValidationStatus(jobId);
        if (cancelled) return;

        setJob(nextJob);

        if (nextJob.done) {
          setJobId(null);
          if (nextJob.runId) {
            await loadRun(nextJob.runId, true);
          }
        }
      } catch (err: any) {
        if (cancelled) return;
        setError(getErrorMessage(err));
        setJobId(null);
      }
    };

    void poll();
    const intervalId = window.setInterval(() => {
      void poll();
    }, 1000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [jobId, loadRun]);

  return {
    dialogOpen,
    closeDialog,
    job,
    run,
    error,
    starting,
    loadingRun,
    isRunning: starting || Boolean(jobId),
    startRun,
    openRun,
    downloadRun,
  };
}
